import { useCallback, useEffect, useRef, useState } from "react";
import { EDITOR_LIMITS } from "@/payroll/editor/constants";
import type { PayrollEditorQueries } from "@/payroll/editor/query";
import { editorError, errorStatus } from "@/payroll/editor/state";
import type {
  DraftMessageAttachment,
  QueuedUpload,
} from "@/payroll/editor/types";
import {
  draftMessageAttachments,
  enrichAttachments,
} from "./enrich-attachments";
import { fileError, fileFingerprint } from "./upload-files";
import {
  attachmentCount,
  enrichUploads,
  restoreUploads,
  saveUploadReceipts,
  restoreDraftUploadIds,
  saveDraftUploadIds,
} from "./upload-state";

export const useEditorUpload = (
  storageKey: string,
  mutation: PayrollEditorQueries["upload"],
  documents: PayrollEditorQueries["documents"],
  remove: PayrollEditorQueries["remove"],
  editable: boolean,
  sentUploadIds: string[],
) => {
  const [items, setItems] = useState(() => restoreUploads(storageKey));
  const [recentIds, setRecentIds] = useState(() =>
    restoreDraftUploadIds(storageKey, items),
  );
  const [recentFiles, setRecentFiles] = useState<Record<string, File>>({});
  const { mutateAsync: uploadFile } = mutation;
  const records = useRef(items);
  const inFlight = useRef(false);
  const [errors, setErrors] = useState<string[]>([]);
  useEffect(
    () => saveDraftUploadIds(storageKey, recentIds),
    [storageKey, recentIds],
  );
  const write = useCallback(
    (next: QueuedUpload[]) => {
      records.current = next;
      setItems(next);
      saveUploadReceipts(storageKey, next);
    },
    [storageKey],
  );
  const update = useCallback(
    (id: string, change: Partial<QueuedUpload>) => {
      write(
        records.current.map((item) =>
          item.clientId === id ? { ...item, ...change } : item,
        ),
      );
    },
    [write],
  );
  const forget = useCallback(
    (id: string) => {
      write(records.current.filter((item) => item.clientId !== id));
    },
    [write],
  );
  const available = editable && documents.isSuccess && !remove.isPending;

  useEffect(() => {
    if (!documents.isSuccess) return;
    const drafts = documents.data.flatMap((file) =>
      file.submitted === false &&
      file.client_upload_id &&
      !sentUploadIds.includes(file.client_upload_id)
        ? [file.client_upload_id]
        : [],
    );
    setRecentIds((ids) =>
      drafts.some((id) => !ids.includes(id))
        ? [...new Set([...ids, ...drafts])]
        : ids,
    );
  }, [documents.isSuccess, documents.data, sentUploadIds]);

  useEffect(() => {
    if (!documents.isSuccess || inFlight.current) return;
    const saved = new Set(
      documents.data.map((document) => document.client_upload_id),
    );
    const remaining = records.current.filter(
      (item) =>
        !(
          item.uncertain &&
          !item.file &&
          !item.documentId &&
          item.status === "failed" &&
          saved.has(item.clientId)
        ),
    );
    if (remaining.length !== records.current.length) write(remaining);
  }, [documents.isSuccess, documents.data, write]);

  useEffect(() => {
    if (!available || inFlight.current) return;
    const next = items.find((item) => item.status === "queued" && item.file);
    if (!next?.file) return;
    inFlight.current = true;
    update(next.clientId, { status: "uploading" });
    uploadFile({ file: next.file, clientId: next.clientId })
      .then(() => forget(next.clientId))
      .catch((error: unknown) =>
        update(next.clientId, {
          status: "failed",
          error: editorError(error),
          uncertain: ![400, 401, 403, 404, 409, 422].includes(
            errorStatus(error) ?? 0,
          ),
        }),
      )
      .finally(() => {
        inFlight.current = false;
      });
  }, [available, items, uploadFile, update, forget]);

  const selectFiles = (files: File[]) => {
    if (!available) {
      setErrors([
        "Files cannot change while a request is active, files are loading, or the proposal is closed.",
      ]);
      return;
    }
    const rejected: string[] = [];
    const selected: string[] = [];
    const selectedFiles: Record<string, File> = {};
    const next = [...records.current];
    for (const file of files) {
      const error = fileError(file);
      if (error) {
        rejected.push(error);
        continue;
      }
      const fingerprint = fileFingerprint(file);
      const index = next.findIndex((item) => item.fingerprint === fingerprint);
      const existing = next[index];
      if (existing) {
        selected.push(existing.clientId);
        selectedFiles[existing.clientId] = file;
        if (existing.status === "failed")
          next[index] = {
            ...existing,
            file,
            status: "queued",
            error: undefined,
          };
        continue;
      }
      if (attachmentCount(documents.data ?? [], next) >= EDITOR_LIMITS.files) {
        rejected.push(
          `${file.name}: A proposal can have 20 files. Remove a file before adding another.`,
        );
        continue;
      }
      const clientId = crypto.randomUUID();
      selected.push(clientId);
      selectedFiles[clientId] = file;
      next.push({
        fingerprint,
        clientId,
        name: file.name,
        file,
        status: "queued",
      });
    }
    setRecentIds((ids) => [...new Set([...ids, ...selected])]);
    setRecentFiles((files) => ({ ...files, ...selectedFiles }));
    setErrors(rejected);
    write(next);
  };
  const forgetRecent = (id: string) => {
    setRecentIds((ids) => ids.filter((entry) => entry !== id));
    setRecentFiles((files) => {
      const { [id]: _removed, ...remaining } = files;
      return remaining;
    });
  };
  const discard = async (id: string) => {
    if (!available || inFlight.current) return;
    const item = records.current.find((entry) => entry.clientId === id);
    if (!item || ["uploading", "removing"].includes(item.status)) return;
    if (!item.uncertain) {
      forget(id);
      forgetRecent(id);
      return;
    }
    inFlight.current = true;
    update(id, { status: "removing" });
    try {
      // Match the saved file by upload identity. Replay the original request
      // only when the list cannot resolve it; filenames are not identities.
      const refreshed = await documents.refetch();
      if (refreshed.isError) throw refreshed.error;
      let documentId =
        item.documentId ??
        refreshed.data?.find((document) => document.client_upload_id === id)
          ?.id;
      if (!documentId && item.file) {
        try {
          const document = await uploadFile({ file: item.file, clientId: id });
          documentId = document.id;
        } catch (error) {
          if (errorStatus(error) !== 404) throw error;
          // The receipt can outlive its archived file. Verify the workspace
          // is still readable before treating a missing file as removed.
          const result = await documents.refetch();
          if (result.isError) throw result.error;
          forget(id);
          forgetRecent(id);
          return;
        }
      }
      if (documentId && !item.removed) {
        update(id, { documentId });
        try {
          await remove.mutateAsync(documentId);
        } catch (error) {
          // A repeated delete can confirm an earlier delete whose reply was lost.
          if (errorStatus(error) !== 404) throw error;
          remove.reset();
        }
        update(id, { removed: true });
      }
      const result = await documents.refetch();
      if (result.isError) throw result.error;
      forget(id);
      forgetRecent(id);
    } catch (error) {
      update(id, { status: "failed", error: editorError(error) });
    } finally {
      inFlight.current = false;
    }
  };
  const retry = (id: string) => {
    if (!available) return;
    const item = records.current.find((entry) => entry.clientId === id);
    if (!item?.file || item.removed || item.status !== "failed") return;
    if (item.documentId) void discard(id);
    else update(id, { status: "queued", error: undefined });
  };
  return {
    items: enrichUploads(items),
    attachments: draftMessageAttachments(
      enrichAttachments(
        documents.data ?? [],
        enrichUploads(items),
        recentFiles,
      ),
      recentIds,
    ),
    recentIds,
    recentFiles,
    forgetRecent,
    clearRecent: () => {
      setRecentIds([]);
      setRecentFiles({});
    },
    restoreDraft: (files: DraftMessageAttachment[] = []) => {
      setRecentIds((ids) => [
        ...new Set([
          ...ids,
          ...files.flatMap((file) =>
            file.client_upload_id ? [file.client_upload_id] : [],
          ),
        ]),
      ]);
      setRecentFiles((current) => ({
        ...current,
        ...Object.fromEntries(
          files.flatMap((file) =>
            file.client_upload_id && file.file
              ? [[file.client_upload_id, file.file]]
              : [],
          ),
        ),
      }));
    },
    errors,
    selectFiles,
    retry,
    discard,
    available,
    full: attachmentCount(documents.data ?? [], items) >= EDITOR_LIMITS.files,
    hasPending: items.length > 0,
    isUploading: items.some((item) =>
      ["uploading", "removing"].includes(item.status),
    ),
  };
};

export type EditorUpload = ReturnType<typeof useEditorUpload>;
